import { List } from 'immutable';
import { checkedFetchAccessTokenIfNeeded } from '~/AccessToken';
import {
	CustomerDatasourceLicence,
	findCustomerDatasourceLicences,
	LicencesResult,
} from '~/Api/Customer/DatasourceLicence';
import { invokeApi, tryResolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { ThunkAction } from '~/Store';

import {
	FAIL_CUSTOMER_DATASOURCE_LICENCES_FETCH,
	RECEIVE_CUSTOMER_DATASOURCE_LICENCES,
	REQUEST_CUSTOMER_DATASOURCE_LICENCES,
} from './ActionTypes';
import { buildCustomerLicensesKey, State as LicenseState } from './reducer';

export interface RequestCustomerDatasourceLicensesAction {
	type: typeof REQUEST_CUSTOMER_DATASOURCE_LICENCES;
	request: Promise<LicencesResult>;
	customerId: string;
}
export interface ReceiveCustomerDatasourceLicensesAction {
	type: typeof RECEIVE_CUSTOMER_DATASOURCE_LICENCES;
	request: Promise<LicencesResult>;
	customerId: string;
	licenses: List<CustomerDatasourceLicence>;
}
export interface FailCustomerDatasourceLicensesFetchAction {
	type: typeof FAIL_CUSTOMER_DATASOURCE_LICENCES_FETCH;
	request: Promise<LicencesResult>;
	customerId: string;
}

export type CustomerDataousrceLicenceAction =
	| RequestCustomerDatasourceLicensesAction
	| ReceiveCustomerDatasourceLicensesAction
	| FailCustomerDatasourceLicensesFetchAction;

export function fetchCustomerDatasourceLicences(customerId: string): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const request = dispatch(invokeApi(findCustomerDatasourceLicences, customerId));
		dispatch(requestCustomerDatasourceLicences(request, customerId));

		await tryResolve(request, (response, error) => {
			if (response) {
				dispatch(receiveCustomerDatasourceLicences(request, customerId, List(response?.items || [])));
			} else {
				dispatch(failCustomerDatasourceLicencesFetch(request, customerId));
				dispatch(handleError(error));
			}
		});
	};
}

function requestCustomerDatasourceLicences(
	request: Promise<LicencesResult>,
	customerId: string
): RequestCustomerDatasourceLicensesAction {
	return {
		type: REQUEST_CUSTOMER_DATASOURCE_LICENCES,
		request,
		customerId,
	};
}

function receiveCustomerDatasourceLicences(
	request: Promise<LicencesResult>,
	customerId: string,
	licenses: List<CustomerDatasourceLicence>
): ReceiveCustomerDatasourceLicensesAction {
	return {
		type: RECEIVE_CUSTOMER_DATASOURCE_LICENCES,
		request,
		customerId,
		licenses,
	};
}

function failCustomerDatasourceLicencesFetch(
	request: Promise<LicencesResult>,
	customerId: string
): FailCustomerDatasourceLicensesFetchAction {
	return {
		type: FAIL_CUSTOMER_DATASOURCE_LICENCES_FETCH,
		request,
		customerId,
	};
}

export function fetchCurrentCustomerDatasourceLicencesIfNeeded(): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		const state = getState();
		const accessTokenResponse = await dispatch(checkedFetchAccessTokenIfNeeded());
		if (!accessTokenResponse) {
			return;
		}

		const { customer } = accessTokenResponse.accessToken.user;
		const licenses = state.customerDatasourceLicences.getIn(
			buildCustomerLicensesKey(customer.id),
			null
		) as ReceiveCustomerDatasourceLicensesAction;
		if (licenses !== null) {
			await licenses.request;
			return;
		}

		await dispatch(fetchCustomerDatasourceLicences(customer.id));
	};
}
